import React, { ReactElement, useEffect, useState } from 'react';
import { Alert, TileRadioAccessible, ReviewAccordionFooter } from '@components';
import { Field, getIn, useFormikContext } from 'formik';
import { tile } from '@interfaces/interfaces';
import { iReviewFormValues } from '@services/ReviewService';

import gridStyles from '@pages/forms/Grid.module.scss';
import InfoIcon from '../../../../../images/info.svg';
import ErrorIcon from '../../../../../images/issue.svg';
import styles from './Step6Grounds.module.scss';

const MISTAKE_OF_IDENTITY_FIELD_NAME = 'SelectedGroundInfo2';
const FIELD_NAME = 'selectTheOption';

const MistakeOfIdentity = (): ReactElement => {
  const { errors, touched, values }: { errors:any, touched: any, values: iReviewFormValues } = useFormikContext();
  const [exceptionalCicumstancesErrorCount, setExceptionalCicumstancesErrorCount] = useState(0);
  const [errorsBlockingSelection, setErrorsBlockingSelection] = useState(false);
  const formikContext = useFormikContext();

  const TILE_DATA: tile[] = [
    {
      body: 'Yes',
      value: 'true',
    },
    {
      body: 'No',
      value: 'false',
    },
  ];
  useEffect(() => {
    formikContext.setFieldValue('errorOrInfoAppearsInAccordion', exceptionalCicumstancesErrorCount);
    if (values.mistakeOfIndentityWasSomeoneElseDriving === 'true' || values.wasThereAnIdentificationMistake === 'false') {
      if (getIn(values, FIELD_NAME).includes(MISTAKE_OF_IDENTITY_FIELD_NAME)) formikContext.setFieldValue(FIELD_NAME, []);
      setErrorsBlockingSelection(true);
    }
    if (values.mistakeOfIndentityWasSomeoneElseDriving === 'false' && values.wasThereAnIdentificationMistake === 'true') setErrorsBlockingSelection(false);
    setExceptionalCicumstancesErrorCount(exceptionalCicumstancesErrorCount + 1);
  },
  [
    values.wasThereAnIdentificationMistake,
    values.mistakeOfIdentityCanUploadSupportingDocs,
    values.mistakeOfIndentityWasSomeoneElseDriving,
  ]);

  const wasThereAnIdentificationMistakeTitle = (
    <div className={getIn(errors, 'wasThereAnIdentificationMistake') && getIn(touched, 'wasThereAnIdentificationMistake') && styles.titleError}>
      <p><b>Do you believe an identification mistake was made when the fine was issued? </b></p>
      <p className={styles.subtitle}>
        Some examples of mistake of identity:
        <ul>
          <li>someone pretended to be you and falsely used your details</li>
          {values.isEnforcementReviewStage && <li>someone has given your details to police, and there has been a mistake</li>}
          {values.isInternalReviewStage && <li>someone gave your details to police, and there has been a mistake</li>}
          <li>you have been a victim of identity theft.</li>
        </ul>
      </p>
    </div>
  );
  const mistakeOfIdentityCanUploadSupportingDocsTitle = (
    <div className={getIn(errors, 'mistakeOfIdentityCanUploadSupportingDocs') && getIn(touched, 'mistakeOfIdentityCanUploadSupportingDocs') && styles.titleError}>
      <p><b>Can you upload documents to support this? </b></p>
      <p className={styles.subtitle}>
        Some examples of supporting documents:
        <ul>
          <li>documents showing that you were not present when the officer issued the offence</li>
          <li>other documentation showing that a mistake has been made.</li>
        </ul>
      </p>
    </div>
  );
  const mistakeOfIndentityWasSomeoneElseDrivingTitle = (
    <div className={getIn(errors, 'mistakeOfIndentityWasSomeoneElseDriving') && getIn(touched, 'mistakeOfIndentityWasSomeoneElseDriving') && styles.titleError}>
      <p><b>Are you the vehicle owner but someone else was driving at the time of the offence?</b></p>
      <p className={styles.subtitle}>
        For example:
        <ul>
          <li>the vehicle is registered in your name, but a family member or friend was driving</li>
          <li>the vehicle is registered to a business, and an employee or associate was driving </li>
        </ul>
      </p>
    </div>
  );

  const wasThereAnIdentificationMistakeMessage = (
    <p>Based on the information provided, you are not eligible to request a review under this ground.</p>
  );
  const mistakeOfIdentityCanUploadSupportingDocsMessage = (
    <p>Your application may not be successful unless you provide evidence.</p>
  );
  const mistakeOfIndentityWasSomeoneElseDrivingIRMessage = (
    <>
      <p><b>You should not submit a review. Instead, you should nominate the driver so that we can re-issue the fine to them.</b></p>
      <br />
      <p>You should do this quickly, before the fine becomes a Notice of Final Demand - after that, it is too late to nominate. For some fines, you need to nominate the driver within 28 days, to avoid cancellation of licence.</p>
      <br />
      <p>If your nomination is accepted, the fine will be withdrawn and may be reissued. </p>
    </>
  );
  const mistakeOfIndentityWasSomeoneElseDrivingERMessage = (
    <>
      <p><b>As this fine has now progressed to a Notice of Final Demand, it is too late to nominate the person who was driving.</b></p>
      <br />
      <p>
        See
        {' '}
        <a href="/Your-options/If-you-didnt-know-about-your-fine" target="_blank" rel="noopener noreferrer">
          If you didn't know about your fine
        </a>
        {' '}
        to find out the options available to you.
      </p>
    </>
  );

  return (
    <div>
      <div className={gridStyles.form}>
        {wasThereAnIdentificationMistakeTitle}
        <Field
          name="wasThereAnIdentificationMistake"
          title=""
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
        />
        <div className={styles.alertMessageBox}>
          {values.wasThereAnIdentificationMistake === 'false' && (
            <Alert
              message={wasThereAnIdentificationMistakeMessage}
              theme="error"
              title=""
              icon={ErrorIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
        </div>
        {mistakeOfIdentityCanUploadSupportingDocsTitle}
        <Field
          name="mistakeOfIdentityCanUploadSupportingDocs"
          title=""
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
        />
        <div className={styles.alertMessageBox}>
          {values.mistakeOfIdentityCanUploadSupportingDocs === 'false' && (
            <Alert
              message={mistakeOfIdentityCanUploadSupportingDocsMessage}
              theme="info"
              title=""
              icon={InfoIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
        </div>
        {mistakeOfIndentityWasSomeoneElseDrivingTitle}
        <Field
          name="mistakeOfIndentityWasSomeoneElseDriving"
          title=""
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
        />
        <div className={styles.alertMessageBox}>
          {values.mistakeOfIndentityWasSomeoneElseDriving === 'true' && values.isInternalReviewStage && (
            <Alert
              message={mistakeOfIndentityWasSomeoneElseDrivingIRMessage}
              theme="error"
              title=""
              icon={ErrorIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
          {values.mistakeOfIndentityWasSomeoneElseDriving === 'true' && values.isEnforcementReviewStage && (
            <Alert
              message={mistakeOfIndentityWasSomeoneElseDrivingERMessage}
              theme="error"
              title=""
              icon={ErrorIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
        </div>
        <br />
        <span><b>Things to note:</b></span>
        {values.isInternalReviewStage && (
        <ul>
          <li>If you believe a mistake of identity was made, you will need to provide evidence.</li>
          <br />
          <li>
            If someone else was driving your vehicle at the time of the offence, you should not select this option. Instead, you should
            {' '}
            <a href="/Your-options/Nominate-driver" target="_blank" rel="noopener noreferrer">
              nominate the driver
            </a>
            {' '}
            so that we can re-issue the fine to them.
          </li>
        </ul>
        )}
        {values.isEnforcementReviewStage && (
        <ul>
          <li>If you believe a mistake of identity was made, you will need to provide evidence, or your application may not be successful.</li>
          <br />
          <li>
            If someone else was driving your vehicle at the time of the offence, you may be able to
            {' '}
            <a href="/Your-options/Infringement-extension" target="_blank" rel="noopener noreferrer">
              apply for an infringement extension
            </a>
            {' '}
            to give you more time to deal with the fine. This will allow you to nominate the driver who was responsible for the fine.
          </li>
        </ul>
        )}

        <ReviewAccordionFooter
          selectOptionName={FIELD_NAME}
          closeOptionName="closeTheOption"
          itemOption={MISTAKE_OF_IDENTITY_FIELD_NAME}
          isSelectButtonDisabled={errorsBlockingSelection}
          validateFieldsNames={['wasThereAnIdentificationMistake', 'mistakeOfIdentityCanUploadSupportingDocs', 'mistakeOfIndentityWasSomeoneElseDriving']}
          isMultiSelect={false}
        />
      </div>
    </div>
  );
};

export default MistakeOfIdentity;
